// src/hooks/useAssetActions.ts
import { useCallback } from 'react';
import { useAssetApi } from '../../api/useAssetApi';

// Defined outside the hook so it is shared across all components that use this hook
const recentRequests = new Set<string>();

const getErrorMessage = (error: any) => {
  const responseData = error?.response?.data;

  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }

  const candidateMessage = responseData?.message
    ?? (typeof responseData?.error === 'string' ? responseData.error : null)
    ?? responseData?.error?.message
    ?? responseData?.errorMessage
    ?? error?.message;

  return typeof candidateMessage === 'string' && candidateMessage.trim()
    ? candidateMessage
    : null;
};

type ReprocessStatus = 'started' | 'duplicate' | 'locked' | 'failed';

interface ReprocessOptions {
  notify?: boolean;
}

interface ReprocessResult {
  status: ReprocessStatus;
}

export const useAssetActions = () => {
  const { reprocessAssetApi, secondaryProcessAssetApi } = useAssetApi();

  const reprocessAsset = useCallback(async (
    pk: string,
    sk: string,
    target: string,
    options: ReprocessOptions = {}
  ): Promise<ReprocessResult> => {
    const { notify = true } = options;
    const requestIdentifier = `${pk}-${target}`;

    if (recentRequests.has(requestIdentifier)) {
      console.log(`Reprocess already requested for ${requestIdentifier}.`);
      return { status: 'duplicate' };
    }

    try {
      recentRequests.add(requestIdentifier);

      setTimeout(() => {
        recentRequests.delete(requestIdentifier);
      }, 30000);

      await reprocessAssetApi({ pk, sk, target });
      if (notify && target !== 'thumbnails') {
        alert(`Reprocessing for ${target} started.`);
      }

      return { status: 'started' };

    } catch (error: any) {
      console.error("Reprocess Error:", error);
      recentRequests.delete(requestIdentifier);

      if (error.response?.status === 423) {
        if (notify) {
          alert("Asset is currently locked. Please try again in a few minutes.");
        }
        return { status: 'locked' };
      } else {
        if (notify) {
          alert("Failed to trigger reprocessing.");
        }
        return { status: 'failed' };
      }
    }
  }, [reprocessAssetApi]);

  const secondaryProcessAsset = useCallback(async (pk: string, suppressAlert = false) => {
    const requestIdentifier = `${pk}-secondaryProcess`;

    if (recentRequests.has(requestIdentifier)) {
      console.log(`Secondary processing already requested for ${requestIdentifier}.`);
      return;
    }

    try {
      recentRequests.add(requestIdentifier);

      setTimeout(() => {
        recentRequests.delete(requestIdentifier);
      }, 30000);

      await secondaryProcessAssetApi({ pk });
      if (!suppressAlert) {
        alert("Secondary processing started.");
      }
    } catch (error: any) {
      console.error("Secondary Process Error:", error);
      const errorMessage = getErrorMessage(error);

      if (error.response?.status === 401) {
        alert("You are not authorized to run secondary processing.");
      } else if (error.response?.status === 423) {
        alert("Asset is currently locked. Please try again in a few minutes.");
      } else if (errorMessage) {
        alert(errorMessage);
      } else {
        alert("Failed to trigger secondary processing.");
      }

      recentRequests.delete(requestIdentifier);
    }
  }, [secondaryProcessAssetApi]);

  return { reprocessAsset, secondaryProcessAsset };
};
